// ==========================================================================
// PWA Install - Simple modal (once per session) + persistent top banner
// ==========================================================================

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { track } from '@/lib/mixpanel';
import { isTelegramWebApp } from '@/lib/platform';

/**
 * Simple install modal — shown once per session.
 * Triggers native install on supported browsers, dismisses otherwise.
 */
export function PWAInstallModal({ onDismiss }: { onDismiss: () => void }) {
  const { t } = useTranslation();

  useEffect(() => {
    track('FE PWA Install Prompt Shown');
  }, []);

  const handleInstall = async () => {
    track('FE PWA Install Accepted');
    const prompt = window.__pwaInstallPrompt;
    if (prompt) {
      try {
        await prompt.prompt();
        await prompt.userChoice;
      } catch {
        // prompt already used or error — just dismiss
      }
      window.__pwaInstallPrompt = null;
    }
    onDismiss();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40">
      <div className="w-full max-w-sm mx-4 mb-8 rounded-2xl bg-white p-6 shadow-xl animate-slide-up">
        <h2 className="text-lg font-semibold text-gray-900 text-center">
          {t('pwa.installTitle')}
        </h2>
        <ul className="mt-4 space-y-2 text-sm text-gray-700">
          <li className="flex items-start gap-2">
            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-700" />
            {t('pwa.benefit1')}
          </li>
          <li className="flex items-start gap-2">
            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-700" />
            {t('pwa.benefit2')}
          </li>
          <li className="flex items-start gap-2">
            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-700" />
            {t('pwa.benefit3')}
          </li>
          <li className="flex items-start gap-2">
            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-700" />
            {t('pwa.benefit4')}
          </li>
        </ul>

        <button
          onClick={handleInstall}
          className="mt-6 w-full rounded-full py-3 text-sm font-semibold text-white uppercase tracking-wide"
          style={{ backgroundColor: 'var(--brand-primary)' }}
        >
          {t('common.install')}
        </button>
        <button
          onClick={onDismiss}
          className="mt-3 w-full rounded-full border border-gray-300 bg-white py-3 text-sm font-semibold text-gray-900 uppercase tracking-wide"
        >
          {t('common.cancel')}
        </button>
      </div>
    </div>
  );
}

/**
 * Slim top banner always visible on Home so users can install anytime.
 */
export function PWAInstallBanner() {
  const { t } = useTranslation();
  const [showTip, setShowTip] = useState(false);

  const handleInstall = async () => {
    const prompt = window.__pwaInstallPrompt;
    if (prompt) {
      try {
        await prompt.prompt();
        await prompt.userChoice;
      } catch {
        // prompt already used or error
      }
      window.__pwaInstallPrompt = null;
    } else {
      setShowTip((v) => !v);
    }
  };

  return (
    <div>
      <div
        className="flex items-center justify-between px-4 py-2"
        style={{ backgroundColor: 'var(--brand-primary)' }}
      >
        <span className="text-xs font-medium text-white">{t('pwa.installBanner')}</span>
        <button
          onClick={handleInstall}
          className="text-xs font-semibold text-white bg-white/20 rounded-full px-3 py-1"
        >
          {t('common.install')}
        </button>
      </div>
      <div
        className="grid transition-all duration-300 ease-in-out"
        style={{ gridTemplateRows: showTip ? '1fr' : '0fr' }}
      >
        <div className="overflow-hidden">
          <div className="flex items-start gap-2 bg-gray-100 text-gray-900 px-4 py-3 text-xs leading-relaxed">
            <span className="flex-1">
              {t('pwa.installTip')}
            </span>
            <button onClick={() => setShowTip(false)} className="text-gray-400 font-bold shrink-0">
              ✕
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Check if running as installed PWA (synchronous, safe for state init) */
function checkIsInstalled(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: window-controls-overlay)').matches ||
    window.matchMedia('(display-mode: minimal-ui)').matches ||
    window.matchMedia('(display-mode: fullscreen)').matches ||
    (navigator as unknown as { standalone?: boolean }).standalone === true ||
    document.referrer.includes('android-app://')
  );
}

/**
 * Hook: shows modal once per session, then banner always.
 * Detects installed PWA on first render (no flash).
 */
export function usePWAInstall() {
  // Eagerly resolve installed state BEFORE first render — prevents banner flash
  const [installed, setInstalled] = useState(checkIsInstalled);
  const [showModal, setShowModal] = useState(false);
  const [modalDismissed, setModalDismissed] = useState(() => {
    if (checkIsInstalled()) return false;
    return !!sessionStorage.getItem('pwa-install-seen');
  });
  const timerStarted = useRef(false);

  // Start 20s modal timer (only if not installed and modal not yet seen)
  useEffect(() => {
    if (installed || modalDismissed || timerStarted.current) return;
    timerStarted.current = true;
    const timer = setTimeout(() => setShowModal(true), 20_000);
    return () => clearTimeout(timer);
  }, [installed, modalDismissed]);

  // Hide everything when the app is installed (mid-session)
  useEffect(() => {
    const onInstalled = () => {
      setInstalled(true);
      setShowModal(false);
    };
    window.addEventListener('appinstalled', onInstalled);
    return () => window.removeEventListener('appinstalled', onInstalled);
  }, []);

  // Also detect display-mode changes (e.g. user installs via browser menu)
  useEffect(() => {
    const mq = window.matchMedia('(display-mode: standalone)');
    const onChange = (e: MediaQueryListEvent) => {
      if (e.matches) {
        setInstalled(true);
        setShowModal(false);
      }
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const dismissModal = () => {
    setShowModal(false);
    setModalDismissed(true);
    sessionStorage.setItem('pwa-install-seen', '1');
  };

  // Telegram Mini-App users already run inside Telegram's webview — there
  // is nothing to install, and the prompt would be confusing. Suppress
  // both the modal and the banner unconditionally on that platform.
  const inTelegram = isTelegramWebApp();

  return {
    showModal: !inTelegram && showModal && !installed,
    showBanner: !inTelegram && modalDismissed && !installed,
    dismissModal,
  };
}
